// ── 4 Modelle ── Zustand und Rumpf (rein).

import type { Catalog, Tenant } from "../../api/types";
import { splitList } from "../../lib/format";
import { ENDPOINT_KIND } from "../../lib/labels";
import { pickOption, type SelectOptions } from "../../ui/select-menu";
import { flowsFromTenant, flowsToBody, type FlowsBody, type FlowsState } from "./flow-models";

export const DEFAULT_ENDPOINT: SelectOptions = [["platform", "Plattform-Key"], ["own", "Eigener Endpoint"]];

/** Adressen, die man nicht nachschlagen will — jede andere OpenAI-kompatible geht auch. */
export const GATEWAY_PRESETS: ReadonlyArray<readonly [string, string]> = [
  ["Requesty EU", "https://router.eu.requesty.ai"],
  ["Requesty", "https://router.requesty.ai"],
  ["OpenAI", "https://api.openai.com"],
];

export function kindOptions(cat: Catalog): SelectOptions {
  return cat.endpoint_kinds.map((k): readonly [string, string] => [k, ENDPOINT_KIND[k] ?? k]);
}

/**
 * Ein Standardmodell außerhalb einer (nicht leeren) Liste erlaubter Modelle
 * stünde im Picker der Instanz nicht zur Wahl — es wird geleert. Die
 * Vanilla-Fassung tat das schon beim ersten Zeichnen; deshalb auch hier im
 * Anfangszustand.
 */
export function normalizeDefault(allowed: readonly string[], defaultModel: string): string {
  return allowed.length && defaultModel && !allowed.includes(defaultModel) ? "" : defaultModel;
}

/** Die Liste, wie der Picker sie hält: getrimmt, ohne Leere. */
const cleanList = (list: readonly string[]): string[] => list.map((x) => x.trim()).filter(Boolean);

export interface ModelsState extends FlowsState {
  p_enabled: boolean;
  p_kind: string;
  p_budget: string;
  p_default_model: string;
  p_allowed: string[];
  o_enabled: boolean;
  o_name: string;
  o_kind: string;
  o_base_url: string;
  o_token: string;
  o_default_model: string;
  o_allowed: string[];
  default: string;
}

export interface ModelsBody {
  models: {
    platform: { enabled: boolean; kind: string; budget_usd: number; default_model: string; allowed_models: string[] };
    own: { enabled: boolean; name: string; kind: string; base_url: string; default_model: string; allowed_models: string[] };
    default: "platform" | "own";
    flows: FlowsBody;
  };
  own_token?: string;
}

export function modelsFromTenant(t: Tenant, cat: Catalog): ModelsState {
  const m = t.models;
  const kinds = kindOptions(cat);
  const pAllowed = cleanList(m.platform.allowed_models);
  const oAllowed = cleanList(m.own.allowed_models);
  return {
    p_enabled: m.platform.enabled,
    p_kind: pickOption(m.platform.kind, kinds),
    p_budget: String(m.platform.budget_usd ?? ""),
    p_default_model: normalizeDefault(pAllowed, m.platform.default_model ?? ""),
    p_allowed: pAllowed,
    o_enabled: m.own.enabled,
    o_name: m.own.name ?? "",
    o_kind: pickOption(m.own.kind, kinds),
    o_base_url: m.own.base_url ?? "",
    o_token: "",
    o_default_model: normalizeDefault(oAllowed, m.own.default_model ?? ""),
    o_allowed: oAllowed,
    default: pickOption(m.default, DEFAULT_ENDPOINT),
    ...flowsFromTenant(t),
  };
}

export function modelsToBody(s: ModelsState): ModelsBody {
  const body: ModelsBody = {
    models: {
      platform: {
        // `Number("")` ist 0 — der Server meldet dann „Muss größer als 0 sein".
        enabled: s.p_enabled, kind: s.p_kind, budget_usd: Number(s.p_budget),
        default_model: s.p_default_model, allowed_models: splitList(s.p_allowed.join("\n")),
      },
      own: {
        enabled: s.o_enabled, name: s.o_name, kind: s.o_kind, base_url: s.o_base_url,
        default_model: s.o_default_model, allowed_models: splitList(s.o_allowed.join("\n")),
      },
      default: s.default as "platform" | "own",
      flows: flowsToBody(s),
    },
  };
  if (s.o_token.trim()) body.own_token = s.o_token.trim();
  return body;
}
